<template>
  <div class="cw-chat">
    <!-- Header -->
    <header class="cw-header">
      <div class="cw-header__left">
        <div class="cw-badge" :class="{ online: !loading && !errorText }"></div>
        <div class="cw-title-wrap">
          <div class="cw-title">Wangdee Support</div>
          <div class="cw-subtitle">
            <span v-if="loading">กำลังเชื่อมต่อ…</span>
            <span v-else-if="errorText" class="cw-error">{{ errorText }}</span>
            <span v-else>พร้อมให้บริการ</span>
          </div>
        </div>
      </div>

      <div class="cw-header__right">
        <button class="cw-btn cw-btn--ghost" @click="refresh" :disabled="loading">
          รีเฟรช
        </button>
      </div>
    </header>

    <!-- Body -->
    <section class="cw-body">
      <div class="cw-messages" ref="messages">
        <!-- Empty -->
        <div v-if="!loading && messages.length === 0" class="cw-empty">
          <div class="cw-empty__icon">💬</div>
          <div class="cw-empty__title">เริ่มต้นแชทได้เลย</div>
          <div class="cw-empty__text">
            ฝากคำถามไว้ได้ครับ ทีมงานจะตอบกลับให้เร็วที่สุด
          </div>
        </div>

        <!-- Messages -->
        <div
          v-for="m in normalizedMessages"
          :key="m._key"
          class="cw-row"
          :class="{ me: m.isAdmin }"
        >
          <!-- Avatar ฝั่งลูกค้า (ซ้าย) -->
          <div class="cw-avatar" v-if="!m.isAdmin">
            <template v-if="m.avatarUrl">
              <img :src="m.avatarUrl" :alt="m.displayName" class="cw-avatar__img" />
            </template>
            <template v-else>
              <span>{{ avatarText(m.displayName) }}</span>
            </template>
          </div>

          <div class="cw-bubble-wrap">
            <div class="cw-meta">
              <span class="cw-name">{{ m.displayName }}</span>
              <span class="cw-dot">•</span>
              <span class="cw-time">{{ formatTime(m.createdAt) }}</span>
            </div>

            <div class="cw-bubble" :class="{ me: m.isAdmin, 'cw-bubble--image-only': !m.text && m.attachments.length }">
              <div
                v-if="m.text"
                class="cw-text"
                v-html="escapeAndLinkify(m.text)"
              ></div>

              <div v-if="m.attachments && m.attachments.length" class="cw-attachments">
                <a
                  v-for="att in m.attachments"
                  :key="att.id"
                  :href="att.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="cw-attachment-link"
                >
                  <img
                    v-if="att.isImage"
                    :src="att.url"
                    :alt="m.displayName"
                    class="cw-attachment-image"
                  />
                  <div v-else class="cw-attachment-file">
                    <div class="cw-attachment-file__icon">📎</div>
                    <div class="cw-attachment-file__name">{{ att.fileName || 'ไฟล์แนบ' }}</div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          <!-- Avatar ฝั่งแอดมิน (ขวา) -->
          <div class="cw-avatar cw-avatar--me" v-if="m.isAdmin">
            <template v-if="m.avatarUrl">
              <img :src="m.avatarUrl" :alt="adminDisplayName(m)" class="cw-avatar__img" />
            </template>
            <template v-else>
              <span>{{ avatarText(adminDisplayName(m)) }}</span>
            </template>
          </div>
        </div>

        <!-- Loading skeleton -->
        <div v-if="loading && messages.length === 0" class="cw-skeleton">
          <div class="cw-skel-row">
            <div class="cw-skel-avatar"></div>
            <div class="cw-skel-bubble"></div>
          </div>
          <div class="cw-skel-row me">
            <div class="cw-skel-bubble"></div>
            <div class="cw-skel-avatar"></div>
          </div>
          <div class="cw-skel-row">
            <div class="cw-skel-avatar"></div>
            <div class="cw-skel-bubble"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Composer -->
    <footer class="cw-footer">
      <div v-if="pendingImagePreview" class="cw-preview">
        <div class="cw-preview__card">
          <img :src="pendingImagePreview" alt="preview" class="cw-preview__img" />
          <button
            type="button"
            class="cw-preview__remove"
            @click="clearPendingImage"
            :disabled="sending"
          >
            ลบรูป
          </button>
        </div>
      </div>

      <div class="cw-composer">
        <input
          ref="imageInput"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/jpg"
          class="cw-file-input"
          @change="onPickImage"
        />

        <button
          class="cw-btn cw-btn--ghost cw-btn--attach"
          type="button"
          @click="triggerImagePicker"
          :disabled="sending || !!errorText"
        >
          รูป
        </button>

        <textarea
          v-model="draft"
          class="cw-input"
          rows="1"
          placeholder="พิมพ์ข้อความ(Enter ส่ง / Shift+Enter ขึ้นบรรทัดใหม่)"
          @keydown.enter.exact.prevent="send"
          @keydown.enter.shift.exact.stop
          :disabled="sending || !!errorText"
          ref="composer"
        ></textarea>

        <button
          class="cw-btn cw-btn--primary"
          @click="send"
          :disabled="!canSend"
        >
          <span v-if="sending">กำลังส่ง…</span>
          <span v-else>ส่ง</span>
        </button>
      </div>

      <div class="cw-hint" v-if="conversationId">
        Conversation: <span class="mono">{{ conversationId }}</span>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios'
import { createConsumer } from '@rails/actioncable'

export default {
    name: 'ChatView',

    data() {
        return {
            loading: true,
            sending: false,
            errorText: '',
            conversationId: null,
            contactIdentifier: '',
            pubsubToken: '',
            accountId: null,
            messages: [],
            draft: '',
            pollTimer: null,
            lastFetchedAt: 0,
            pendingImageFile: null,
            pendingImagePreview: '',
            cable: null,
            subscription: null,
            cableConnected: false,
            cableFallbackTimer: null,
        }
    },

    computed: {
        http() {
            return this.$axios || axios
        },

        currentUser() {
            const fromStore =
                (this.$store &&
                    this.$store.state &&
                    (this.$store.state.user ||
                        (this.$store.state.auth && this.$store.state.auth.user))) ||
                null

            const unwrapUser = (payload) => {
                if (!payload || typeof payload !== 'object') return null
                if (payload.user && typeof payload.user === 'object') return payload.user
                return payload
            }

            const normalizedStoreUser = unwrapUser(fromStore)
            if (normalizedStoreUser) return normalizedStoreUser

            try {
                const raw = localStorage.getItem('user')
                if (raw) return unwrapUser(JSON.parse(raw))
            } catch (e) {
                // ignore parse error
            }

            return null
        },

        currentUserId() {
            const u = this.currentUser || {}
            return u.id || u.user_id || null
        },

        currentUserName() {
            const u = this.currentUser || {}
            return (
                u.username ||
                u.name ||
                u.full_name ||
                u.display_name ||
                u.nickname ||
                u.email ||
                'Customer'
            )
        },

        normalizedMessages() {
            const list = Array.isArray(this.messages) ? this.messages : []

            return list
                .map((m, idx) => {
                    const text = this.pickText(m)
                    const createdAt = this.pickCreatedAt(m)
                    const type = this.pickMessageType(m)
                    const isActivity = type === 'activity'
                    const isAdmin = this.resolveIsAdmin(m)

                    const displayName = isAdmin
                        ? (this.pickAgentName(m) || 'Wangdee Admin')
                        : this.currentUserName

                    const attachments = this.pickAttachments(m)

                    return {
                        _key: (m.id || m._id || idx) + '_' + idx,
                        raw: m,
                        text: text,
                        createdAt: createdAt,
                        type: type,
                        isActivity: isActivity,
                        isAdmin: isAdmin,
                        displayName: displayName,
                        attachments: attachments,
                        avatarUrl: this.pickAvatarUrl(m, isAdmin),
                    }
                })
                .filter((m) => !m.isActivity && (m.text || (m.attachments && m.attachments.length)))
        },

        canSend() {
            return (
                !this.loading &&
                !this.sending &&
                !this.errorText &&
                (
                    this.draft.trim().length > 0 ||
                    !!this.pendingImageFile
                )
            )
        },
    },

    watch: {
        conversationId(newVal, oldVal) {
            if (!newVal || newVal === oldVal) return
            this.connectCable()
        },

        messages() {
            this.$nextTick(() => {
                if (this.isNearBottom()) {
                    this.scrollToBottom(false)
                }
            })
        },

        draft() {
            this.autogrow()
        },
    },

    async mounted() {
        await this.bootstrap()
        this.connectCable()
        this.bindPageVisibility()
    },

    beforeDestroy() {
        this.disconnectCable()
        this.stopPolling()
        this.clearCableFallbackTimer()

        if (this._handleVisibilityChange) {
            document.removeEventListener('visibilitychange', this._handleVisibilityChange)
        }
    },

    methods: {
        resetChatState() {
            this.disconnectCable()
            this.stopPolling()
            this.clearCableFallbackTimer()
            this.loading = true
            this.sending = false
            this.errorText = ''
            this.conversationId = null
            this.contactIdentifier = ''
            this.pubsubToken = ''
            this.accountId = null
            this.messages = []
            this.draft = ''
            this.lastFetchedAt = 0
            this.cableConnected = false
        },

        async bootstrap() {
            this.loading = true
            this.errorText = ''

            try {
                let cid = this.loadStoredConversationId()

                if (cid) {
                    this.conversationId = cid
                    try {
                        await this.fetchMessages(true)
                    } catch (e) {
                        this.conversationId = null
                        this.clearStoredConversationId()
                    }
                }

                const initRes = await this.http.get('/api/chat/init')
                const payload = initRes && initRes.data ? initRes.data : {}

                if (!payload.conversationId) {
                    throw new Error('init did not return conversationId')
                }

                this.conversationId = payload.conversationId
                this.contactIdentifier = payload.contactIdentifier || ''
                this.pubsubToken = payload.pubsubToken || ''
                this.accountId = payload.accountId || null

                this.storeConversationId(this.conversationId)
                await this.fetchMessages(true)
            } catch (e) {
                this.errorText = this.humanizeError(e, 'เชื่อมต่อแชทไม่สำเร็จ')
            } finally {
                this.loading = false
                this.$nextTick(() => this.scrollToBottom(true))
                this.autogrow()
            }
        },

        getCableUrl() {
            const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
            return `${protocol}://support.wangdee.co/cable`
        },

        connectCable() {
            if (!this.pubsubToken) {
                console.warn('[chat] no pubsubToken -> fallback polling')
                this.startPolling(5000)
                return
            }

            this.disconnectCable()
            this.clearCableFallbackTimer()
            this.cableConnected = false

            try {
                this.cable = createConsumer(this.getCableUrl())

                this.subscription = this.cable.subscriptions.create(
                    {
                        channel: 'RoomChannel',
                        pubsub_token: this.pubsubToken,
                        account_id: this.accountId || undefined,
                    },
                    {
                        connected: () => {
                            console.log('[chat] websocket connected')
                            this.cableConnected = true
                            this.stopPolling()
                            this.clearCableFallbackTimer()
                        },

                        disconnected: () => {
                            console.warn('[chat] websocket disconnected -> fallback polling')
                            this.cableConnected = false
                            this.startPolling(5000)
                        },

                        rejected: () => {
                            console.warn('[chat] websocket rejected -> fallback polling')
                            this.cableConnected = false
                            this.startPolling(5000)
                        },

                        received: (payload) => {
                            console.log('[chat] websocket payload', payload)
                            this.fetchMessages(true)
                        },
                    }
                )

                this.cableFallbackTimer = setTimeout(() => {
                    if (!this.cableConnected) {
                        console.warn('[chat] websocket timeout -> fallback polling')
                        this.startPolling(5000)
                    }
                }, 8000)
            } catch (e) {
                console.warn('[chat] websocket init failed', e)
                this.cableConnected = false
                this.startPolling(5000)
            }
        },

        disconnectCable() {
            try {
                if (this.subscription) {
                    this.subscription.unsubscribe()
                    this.subscription = null
                }
            } catch (e) {
                console.warn('[chat] websocket unsubscribe failed', e)
            }

            try {
                if (this.cable) {
                    this.cable.disconnect()
                    this.cable = null
                }
            } catch (e) {
                console.warn('[chat] websocket disconnect failed', e)
            }

            this.cableConnected = false
        },

        clearCableFallbackTimer() {
            if (this.cableFallbackTimer) {
                clearTimeout(this.cableFallbackTimer)
                this.cableFallbackTimer = null
            }
        },

        startPolling(interval = 10000) {
            this.stopPolling()

            console.log('[chat] polling start', interval)

            this.pollTimer = setInterval(() => {
                if (!this.loading && !this.sending && this.conversationId) {
                    this.fetchMessages(false)
                }
            }, interval)
        },

        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer)
                this.pollTimer = null
            }
        },

        async refresh() {
            if (!this.conversationId) {
                await this.bootstrap()
                return
            }

            await this.fetchMessages(true)
            this.$nextTick(() => this.scrollToBottom(true))
        },

        async recoverFromForbidden() {
            this.clearStoredConversationId()
            this.resetChatState()
            await this.bootstrap()
            this.connectCable()
        },

        async fetchMessages(forceScroll) {
            try {
                const shouldStickBottom = forceScroll || this.isNearBottom()

                const res = await this.http.get('/api/chat/messages', {
                    params: { conversationId: this.conversationId },
                })

                const data = res && res.data ? res.data : null

                let payload = []
                if (Array.isArray(data)) payload = data
                else if (data && Array.isArray(data.payload)) payload = data.payload
                else if (data && data.data && Array.isArray(data.data.payload)) payload = data.data.payload

                const prevLastId = (this.messages && this.messages.length)
                    ? (this.messages[this.messages.length - 1] && this.messages[this.messages.length - 1].id)
                    : null

                const nextLastId = (payload && payload.length)
                    ? (payload[payload.length - 1] && payload[payload.length - 1].id)
                    : null

                const sameLast = String(prevLastId || '') === String(nextLastId || '')
                const sameLen = (this.messages ? this.messages.length : 0) === payload.length

                if (!sameLast || !sameLen) {
                    this.messages = payload
                }

                this.lastFetchedAt = Date.now()

                await this.$nextTick()

                if (shouldStickBottom) {
                    this.scrollToBottom(true)
                }
            } catch (e) {
                const status = e && e.response ? e.response.status : null

                if (status === 403) {
                    await this.recoverFromForbidden()
                    return
                }

                this.errorText = this.humanizeError(e, 'โหลดข้อความไม่สำเร็จ')
            }
        },

        async send() {
            if (!this.canSend) return

            const text = this.draft.trim()
            const imageFile = this.pendingImageFile
            const imagePreview = this.pendingImagePreview

            this.draft = ''
            this.autogrow()

            const optimistic = {
                id: 'tmp_' + Date.now(),
                content: text,
                created_at: new Date().toISOString(),
                message_type: 'incoming',
                attachments: imageFile ? [
                    {
                        id: 'tmp_att_' + Date.now(),
                        data_url: imagePreview,
                        file_type: imageFile.type,
                        file_name: imageFile.name,
                    }
                ] : [],
            }

            this.messages = (this.messages || []).concat([optimistic])

            await this.$nextTick()
            this.scrollToBottom(true)

            this.sending = true
            this.errorText = ''

            try {
                if (imageFile) {
                    const formData = new FormData()
                    formData.append('conversationId', this.conversationId)
                    formData.append('message', text)
                    formData.append('file', imageFile)

                    await this.http.post('/api/chat/messages/image', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    })

                    this.clearPendingImage()
                } else {
                    await this.http.post('/api/chat/send', {
                        conversationId: this.conversationId,
                        message: text,
                    })
                }

                await this.fetchMessages(true)
            } catch (e) {
                this.errorText = this.humanizeError(e, 'ส่งข้อความไม่สำเร็จ')
                await this.fetchMessages(true)
            } finally {
                this.sending = false
                this.$nextTick(() => this.scrollToBottom(true))
            }
        },

        triggerImagePicker() {
            if (this.$refs.imageInput) {
                this.$refs.imageInput.click()
            }
        },

        onPickImage(event) {
            const file = event && event.target && event.target.files && event.target.files[0]
            if (!file) return

            const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg']
            if (!allowed.includes(file.type)) {
                this.errorText = 'รองรับเฉพาะ JPG, PNG, WEBP'
                this.clearPendingImage()
                return
            }

            this.pendingImageFile = file
            this.errorText = ''

            const reader = new FileReader()
            reader.onload = (e) => {
                this.pendingImagePreview = e && e.target ? e.target.result : ''
            }
            reader.readAsDataURL(file)

            if (this.$refs.imageInput) {
                this.$refs.imageInput.value = ''
            }
        },

        clearPendingImage() {
            this.pendingImageFile = null
            this.pendingImagePreview = ''

            if (this.$refs.imageInput) {
                this.$refs.imageInput.value = ''
            }
        },

        bindPageVisibility() {
            this._handleVisibilityChange = () => {
                if (document.hidden) return
                if (this.conversationId) {
                    this.fetchMessages(false)
                }
            }

            document.addEventListener('visibilitychange', this._handleVisibilityChange)
        },

        loadStoredConversationId() {
            try {
                return localStorage.getItem('chat_conversation_id') || null
            } catch (e) {
                return null
            }
        },

        storeConversationId(id) {
            try {
                localStorage.setItem('chat_conversation_id', String(id))
            } catch (e) {
                // ignore
            }
        },

        clearStoredConversationId() {
            try {
                localStorage.removeItem('chat_conversation_id')
            } catch (e) {
                // ignore
            }
        },

        pickText(m) {
            if (!m) return ''
            return (
                m.content ||
                m.text ||
                m.message ||
                ''
            )
        },

        pickCreatedAt(m) {
            if (!m) return ''
            return (
                m.created_at ||
                m.createdAt ||
                m.timestamp ||
                ''
            )
        },

        pickMessageType(m) {
            if (!m) return ''

            var raw = typeof m.message_type !== 'undefined'
                ? m.message_type
                : (typeof m.messageType !== 'undefined' ? m.messageType : '')

            if (raw === 0 || raw === '0') return 'incoming'
            if (raw === 1 || raw === '1') return 'outgoing'
            if (raw === 2 || raw === '2') return 'activity'
            if (raw === 3 || raw === '3') return 'template'

            return raw
        },

        pickAgentName(m) {
            if (!m) return ''

            return (
                (m.sender && (m.sender.name || m.sender.available_name)) ||
                (m.meta &&
                    m.meta.sender &&
                    (m.meta.sender.name || m.meta.sender.available_name)) ||
                (m.account &&
                    m.account.name) ||
                ''
            )
        },

        pickAvatarUrl(m, isAdmin) {
            if (!m) return ''

            if (isAdmin) {
                return (
                    (m.sender && (m.sender.thumbnail || m.sender.avatar_url)) ||
                    (m.meta &&
                        m.meta.sender &&
                        (m.meta.sender.thumbnail || m.meta.sender.avatar_url)) ||
                    ''
                )
            }

            const u = this.currentUser || {}
            return u.avatar || u.avatar_url || u.photo || ''
        },

        pickAttachments(m) {
            const list = Array.isArray(m && m.attachments) ? m.attachments : []

            return list.map((att, idx) => {
                const fileType = att.file_type || att.data_type || ''
                const url =
                    att.data_url ||
                    att.thumb_url ||
                    att.file_url ||
                    att.url ||
                    ''

                return {
                    id: att.id || idx,
                    url: url,
                    fileName: att.file_name || att.filename || '',
                    isImage: /^image\//.test(fileType) || /\.(png|jpe?g|webp|gif)$/i.test(url),
                }
            }).filter((att) => att.url)
        },

        avatarText(name) {
            if (!name) return '?'
            return String(name).trim().charAt(0).toUpperCase()
        },

        adminDisplayName(m) {
            return this.pickAgentName(m) || 'Admin'
        },

        formatTime(value) {
            if (!value) return ''

            try {
                const date = new Date(value)
                if (Number.isNaN(date.getTime())) return ''
                return date.toLocaleTimeString('th-TH', {
                    hour: '2-digit',
                    minute: '2-digit',
                })
            } catch (e) {
                return ''
            }
        },

        escapeHtml(text) {
            return String(text || '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;')
        },

        escapeAndLinkify(text) {
            const escaped = this.escapeHtml(text || '')
            const withLinks = escaped.replace(
                /(https?:\/\/[^\s<]+)/g,
                '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
            )

            return withLinks.replace(/\n/g, '<br>')
        },

        humanizeError(error, fallback = 'เกิดข้อผิดพลาด') {
            if (!error) return fallback

            if (error.response && error.response.data) {
                const data = error.response.data

                if (typeof data === 'string' && data.trim()) {
                    return data
                }

                if (data.message) {
                    return data.message
                }

                if (data.error) {
                    return data.error
                }
            }

            if (error.message) {
                return error.message
            }

            return fallback
        },

        isNearBottom() {
            const el = this.$refs.messages
            if (!el) return true

            const threshold = 120
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
            return distanceFromBottom <= threshold
        },

        scrollToBottom(force) {
            const el = this.$refs.messages
            if (!el) return

            if (force || this.isNearBottom()) {
                el.scrollTop = el.scrollHeight
            }
        },

        autogrow() {
            this.$nextTick(() => {
                const el = this.$refs.composer
                if (!el) return

                el.style.height = 'auto'
                el.style.height = Math.min(el.scrollHeight, 160) + 'px'
            })
        },
        resolveIsAdmin(m) {
            if (!m || typeof m !== 'object') return false

            const sender =
        m.sender ||
        (m.meta && m.meta.sender) ||
        null

            const senderTypeRaw =
        (m.sender_type ||
            (sender && sender.type) ||
            (sender && sender.sender_type) ||
            ''
        )

            const senderType = String(senderTypeRaw).toLowerCase()

            if (
                senderType.includes('user') ||
        senderType.includes('agent') ||
        senderType.includes('administrator') ||
        senderType.includes('admin')
            ) {
                return true
            }

            if (
                senderType.includes('contact') ||
        senderType.includes('customer')
            ) {
                return false
            }

            // fallback: ถ้ามี sender และชื่อไม่ใช่ user ปัจจุบัน มักเป็น admin
            const senderId = sender && (sender.id || sender.sender_id)
            const currentUserId = this.currentUserId

            if (senderId && currentUserId && String(senderId) !== String(currentUserId)) {
                if (sender && (sender.available_name || sender.name)) {
                    return true
                }
            }

            const type = this.pickMessageType(m)

            if (type === 'outgoing' || type === 1 || type === '1') {
                return true
            }

            if (type === 'incoming' || type === 0 || type === '0') {
                return false
            }

            return false
        },
    },
}
</script>

<style scoped>
.cw-chat {
  min-height: calc(100vh - 12px);
  height: calc(100vh - 12px);
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(circle at top left, rgba(210, 172, 74, 0.10), transparent 28%),
    linear-gradient(180deg, #0d2c24 0%, #12382f 45%, #0b201a 100%);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(214, 177, 90, 0.10);
  padding-top: 30px;
}

.cw-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px 14px;
  background: linear-gradient(180deg, rgba(17, 53, 44, 0.96) 0%, rgba(11, 35, 29, 0.98) 100%);
  border-bottom: 1px solid rgba(214, 177, 90, 0.16);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
  flex: 0 0 auto;
}

.cw-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cw-badge {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.24);
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.05);
}

.cw-badge.online {
  background: #38d996;
  box-shadow:
    0 0 0 4px rgba(56, 217, 150, 0.10),
    0 0 14px rgba(56, 217, 150, 0.35);
}

.cw-title-wrap {
  line-height: 1.2;
}

.cw-title {
  color: #f6e7b0;
  font-weight: 800;
  font-size: 18px;
  letter-spacing: 0.2px;
}

.cw-subtitle {
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  margin-top: 3px;
}

.cw-error {
  color: #ffcf99;
}

.cw-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  padding: 14px 14px 0;
}

.cw-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 14px 140px;
  border-radius: 22px 22px 0 0;
  background:
    radial-gradient(1200px 700px at 10% -10%, rgba(69, 137, 255, 0.08), transparent 60%),
    radial-gradient(900px 500px at 100% 0%, rgba(255, 126, 86, 0.05), transparent 55%),
    radial-gradient(900px 600px at 20% 110%, rgba(120, 80, 255, 0.06), transparent 60%),
    linear-gradient(180deg, rgba(9, 27, 23, 0.86) 0%, rgba(8, 22, 18, 0.94) 100%);
  border: 1px solid rgba(214, 177, 90, 0.10);
  border-bottom: 0;
  scrollbar-width: thin;
  scrollbar-color: rgba(214, 177, 90, 0.32) transparent;
}

.cw-messages::-webkit-scrollbar {
  width: 8px;
}

.cw-messages::-webkit-scrollbar-track {
  background: transparent;
}

.cw-messages::-webkit-scrollbar-thumb {
  background: rgba(214, 177, 90, 0.24);
  border-radius: 999px;
}

.cw-empty {
  margin: 70px auto 0;
  max-width: 360px;
  text-align: center;
  color: rgba(255, 255, 255, 0.75);
}

.cw-empty__icon {
  font-size: 34px;
  margin-bottom: 10px;
}

.cw-empty__title {
  font-weight: 800;
  color: #fff7d8;
  margin-bottom: 6px;
}

.cw-empty__text {
  font-size: 13px;
  line-height: 1.5;
}

.cw-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin: 12px 0;
}

.cw-row.me {
  justify-content: flex-end;
}

.cw-avatar {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: linear-gradient(180deg, #31584c 0%, #1b3c33 100%);
  border: 1px solid rgba(214, 177, 90, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.96);
  font-weight: 800;
  font-size: 12px;
  flex: 0 0 auto;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
}

.cw-avatar--me {
  background: linear-gradient(180deg, #c7982e 0%, #8f6515 100%);
  color: #fffef8;
  border-color: rgba(255, 223, 152, 0.20);
}

.cw-avatar__img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  border-radius: 50%;
}

.cw-bubble-wrap {
  max-width: min(720px, 78%);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cw-row.me .cw-bubble-wrap {
  align-items: flex-end;
}

.cw-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.58);
}

.cw-name {
  color: #f0d995;
  font-weight: 700;
}

.cw-dot {
  opacity: 0.45;
}

.cw-time {
  color: rgba(255, 255, 255, 0.50);
}

.cw-bubble {
  padding: 11px 13px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.96);
  line-height: 1.48;
  word-break: break-word;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.14);
}

.cw-row:not(.me) .cw-bubble {
  border-top-left-radius: 8px;
}

.cw-bubble.me {
  background: linear-gradient(180deg, #d0a23b 0%, #9a6c18 100%);
  border-color: rgba(255, 223, 152, 0.22);
  color: #fffdf7;
  border-top-right-radius: 8px;
}

.cw-bubble--image-only {
  padding-top: 10px;
  padding-bottom: 10px;
}

.cw-text a {
  color: #d7ebff;
  text-decoration: underline;
}

.cw-attachments {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cw-attachment-link {
  display: inline-block;
  text-decoration: none;
}

.cw-attachment-image {
  display: block;
  max-width: 240px;
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.20);
}

.cw-attachment-file {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.07);
  color: #fff;
}

.cw-attachment-file__icon {
  font-size: 18px;
}

.cw-attachment-file__name {
  font-size: 14px;
  word-break: break-word;
}

.cw-footer {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 14px 14px;
  background: linear-gradient(180deg, rgba(11, 35, 29, 0.96) 0%, rgba(8, 24, 19, 0.98) 100%);
  border-top: 1px solid rgba(214, 177, 90, 0.10);
}

.cw-preview {
  margin-bottom: 0;
  max-height: 86px;
  overflow: hidden;
  flex: 0 0 auto;
}

.cw-preview__card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  padding: 8px 10px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(214, 177, 90, 0.10);
}

.cw-preview__img {
  display: block;
  width: 58px;
  height: 58px;
  object-fit: cover;
  border-radius: 10px;
  flex-shrink: 0;
}

.cw-preview__remove {
  border: 0;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  white-space: nowrap;
}

.cw-file-input {
  display: none;
}

.cw-composer {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  padding: 12px;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(14, 42, 35, 0.97) 0%, rgba(10, 30, 24, 0.98) 100%);
  border: 1px solid rgba(214, 177, 90, 0.12);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
}

.cw-input {
  flex: 1;
  resize: none;
  border-radius: 16px;
  padding: 11px 13px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.10);
  color: rgba(255, 255, 255, 0.98);
  outline: none;
  line-height: 1.4;
  min-height: 46px;
  max-height: 140px;
}

.cw-input::placeholder {
  color: rgba(255, 255, 255, 0.48);
}

.cw-btn {
  border: 0;
  border-radius: 14px;
  padding: 0 16px;
  height: 46px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.08s ease, opacity 0.12s ease, box-shadow 0.18s ease;
  user-select: none;
  white-space: nowrap;
}

.cw-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cw-btn:active {
  transform: translateY(1px);
}

.cw-btn--primary {
  background: linear-gradient(180deg, #c7982e 0%, #8f6515 100%);
  color: #fffef8;
  box-shadow: 0 8px 18px rgba(151, 106, 21, 0.28);
}

.cw-btn--ghost {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(214, 177, 90, 0.14);
  color: #f3e6b8;
}

.cw-btn--attach {
  flex: 0 0 auto;
  min-width: 68px;
}

.cw-hint {
  margin-top: 2px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.cw-skeleton {
  margin-top: 10px;
  opacity: 0.85;
}

.cw-skel-row {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  margin: 12px 0;
}

.cw-skel-row.me {
  justify-content: flex-end;
}

.cw-skel-avatar {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.10);
}

.cw-skel-bubble {
  width: 240px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
}

.cw-skel-row.me .cw-skel-bubble {
  width: 200px;
}

@media (max-width: 768px) {
  .cw-chat {
    min-height: calc(100vh - 8px);
    height: calc(100vh - 8px);
    border-radius: 12px;
  }

  .cw-header {
    padding: 16px 14px 12px;
  }

  .cw-title {
    font-size: 16px;
  }

  .cw-body {
    padding: 10px 10px 0;
  }

  .cw-messages {
    padding: 14px 10px 136px;
    border-radius: 18px 18px 0 0;
  }

  .cw-bubble-wrap {
    max-width: 86%;
  }

  .cw-avatar {
    width: 32px;
    height: 32px;
    font-size: 11px;
  }

  .cw-footer {
    padding: 8px 10px 12px;
  }

  .cw-composer {
    gap: 8px;
    padding: 10px;
    border-radius: 16px;
  }

  .cw-btn {
    height: 44px;
    padding: 0 14px;
  }

  .cw-btn--attach {
    min-width: 58px;
  }

  .cw-preview {
    max-height: 76px;
  }

  .cw-preview__img {
    width: 52px;
    height: 52px;
  }
}

@media (max-width: 420px) {
  .cw-title {
    font-size: 15px;
  }

  .cw-subtitle {
    font-size: 11px;
  }

  .cw-bubble-wrap {
    max-width: 90%;
  }

  .cw-messages {
    padding-bottom: 144px;
  }
}
</style>